import { Formik } from "formik";

import { useGetOrganisationById } from "@sso/apis/organisations/hooks";
import { useAddUserMutation, useEditUserMutation } from "@sso/apis/users";
import { useGetUserById } from "@sso/apis/users/hooks";
import { UserFormFields } from "@sso/components/UserFormFields";
import { userFormSchemaValidation } from "@sso/components/UserFormFields/schema";
import { DialogTitle, OrganisationIds, userInitialValues } from "@sso/constant";
import { useAppSelector } from "@sso/hooks/useRedux";
import { selectCurrentUser } from "@sso/store/slices/auth";
import { selectDialog } from "@sso/store/slices/dialog";
import { UserForm } from "@sso/types";
import { CustomForm } from "@sso/ui/CustomForm";
import { Typography } from "@sso/ui/Typography";
import { findSsoRole } from "@sso/utils";

import { DialogActionButtons, DialogBody, DialogHeader } from "../components";

export const UserDialog = () => {
  const { id, data: orgId } = useAppSelector(selectDialog);

  const user = useGetUserById(id as string);
  const currentUser = useAppSelector(selectCurrentUser);
  const org = useGetOrganisationById(orgId as number);

  let selectedUser: UserForm = userInitialValues;
  if (user) {
    const { userId, firstname, lastname, username, email, mobile, roles, organisation } = user;
    selectedUser = {
      userId,
      firstname,
      lastname,
      username,
      email,
      mobile: mobile || "",
      roleId: findSsoRole(roles).id as string,
      organisationId: organisation.id as number,
    };
  }

  const [addUser, { isLoading: addLoading }] = useAddUserMutation();
  const [editUser, { isLoading: editLoading }] = useEditUserMutation();

  const handleSubmit = (values: UserForm) => {
    const userFormValues: UserForm = { ...values };
    if (orgId) {
      userFormValues.organisationId = orgId as number;
    }
    user ? editUser(userFormValues) : addUser(userFormValues);
  };

  return (
    <>
      <DialogHeader title={user ? DialogTitle.EDIT_USER : DialogTitle.ADD_USER} />
      {
        <>
          <Typography width="100%" textAlign="left" padding="0.5rem 2rem">
            {org
              ? `Organisation: ${org.name}`
              : `"${currentUser?.organisation.name}" organisation has been selected for this user.`}
          </Typography>
          <Formik
            onSubmit={handleSubmit}
            initialValues={user ? selectedUser : userInitialValues}
            validationSchema={userFormSchemaValidation}
          >
            <CustomForm width="100%" overflow="hidden">
              <DialogBody display="flex" wrap="wrap" align="baseline">
                <UserFormFields
                  showRoleField
                  showAdminRole={orgId === OrganisationIds.BASEMAP || user?.organisation.id === OrganisationIds.BASEMAP}
                />
              </DialogBody>
              <DialogActionButtons loading={addLoading || editLoading} buttonTitle={user ? "Edit" : "Add"} />
            </CustomForm>
          </Formik>
        </>
      }
    </>
  );
};
